import { FC, useState, useCallback, useEffect, useRef } from 'react';
import { Modal, Button, Group, Text, Stack, TextInput } from '@mantine/core';
import { useMedplumNavigate, useMedplum } from '@medplum/react';
import { ContactPoint, Practitioner } from '@medplum/fhirtypes';
import { useDisclosure } from '@mantine/hooks';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconCircleCheck, IconPlus } from '@tabler/icons-react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { ARKON } from '@consts/systems.consts';
import { useTranslation } from 'react-i18next';
import FormActionButtons, {
  FormActionButtonsRef,
} from '@/components/FormActionButtons';
import { isEqual } from 'lodash';

//------------------------------------------------------------------------------
interface PractitionerFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  workPhone: string;
  qualification: string;
  specialization: string;
  organization: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

//------------------------------------------------------------------------------
interface CreatePractitionerModalProps {
  practitioner?: Practitioner | null;
  opened?: boolean;
  onClose?: () => void;
  onPractitionerCreated?: (practitioner: Practitioner) => void;
}

//------------------------------------------------------------------------------
const CreatePractitionerModal: FC<CreatePractitionerModalProps> = ({
  practitioner = null,
  opened: externalOpened,
  onClose: externalOnClose,
  onPractitionerCreated,
}) => {
  const [internalOpened, { open, close: internalClose }] = useDisclosure(false);
  const opened = externalOpened !== undefined ? externalOpened : internalOpened;
  const close = externalOnClose || internalClose;
  const navigate = useMedplumNavigate();
  const medplum = useMedplum();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation('practitioners');
  const form = useForm<PractitionerFormData>({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      workPhone: '',
      qualification: '',
      specialization: '',
      organization: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      postalCode: '',
      country: t('form.countryDefault'),
    },
    validate: {
      firstName: (value) => (!value ? t('validation.firstNameRequired') : null),
      lastName: (value) => (!value ? t('validation.lastNameRequired') : null),
    },
  });
  const formActionsRef = useRef<FormActionButtonsRef>(null);

  //----------------------------------------------------------------------------
  // Load existing practitioner data when editing
  const loadPractitionerData = useCallback(() => {
    if (practitioner) {
      const organizationExtension = practitioner.extension?.find(
        (ext) => ext.url === ARKON.SD.EXTERNAL_ORGANIZATION_EXTENSION
      );
      const address = practitioner.address?.[0];

      // Get mobile and work phone numbers
      const mobilePhone =
        practitioner.telecom?.find(
          (t) => t.system === 'phone' && t.use === 'mobile'
        )?.value || '';
      const workPhone =
        practitioner.telecom?.find(
          (t) => t.system === 'phone' && t.use === 'work'
        )?.value || '';

      form.setValues({
        firstName: practitioner.name?.[0]?.given?.[0] || '',
        lastName: practitioner.name?.[0]?.family || '',
        email:
          practitioner.telecom?.find((t) => t.system === 'email')?.value || '',
        phone: mobilePhone,
        workPhone: workPhone,
        qualification: practitioner.qualification?.[0]?.code?.text || '',
        specialization:
          practitioner.qualification?.[0]?.code?.coding?.[0]?.display || '',
        organization: organizationExtension?.valueString || '',
        addressLine1: address?.line?.[0] || '',
        addressLine2: address?.line?.[1] || '',
        city: address?.city || '',
        state: address?.state || '',
        postalCode: address?.postalCode || '',
        country: address?.country || t('form.countryDefault'),
      });
    }
  }, [practitioner, t, form]);
  //----------------------------------------------------------------------------
  // Load data when modal opens externally
  const [hasLoadedData, setHasLoadedData] = useState(false);

  useEffect(() => {
    if (externalOpened && practitioner && !hasLoadedData) {
      loadPractitionerData();
      setHasLoadedData(true);
    } else if (!externalOpened) {
      setHasLoadedData(false);
    }
  }, [externalOpened, practitioner, loadPractitionerData, hasLoadedData]);

  //----------------------------------------------------------------------------
  const [initialSnapshot, setInitialSnapshot] =
    useState<PractitionerFormData | null>(null);

  useEffect(() => {
    if (opened) {
      setInitialSnapshot(form.getValues());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  //----------------------------------------------------------------------------
  const onSubmit = async (values: PractitionerFormData): Promise<void> => {
    setLoading(true);
    try {
      // Build telecom array with both phone numbers
      const telecom = [
        {
          system: 'email',
          value: values.email,
          use: 'work',
        },
        {
          system: 'phone',
          value: values.phone,
          use: 'mobile',
        },
      ] as ContactPoint[];

      // Add work phone if provided
      if (values.workPhone) {
        telecom.push({
          system: 'phone',
          value: values.workPhone,
          use: 'work',
        } as ContactPoint);
      }

      const practitionerData: Practitioner = {
        resourceType: 'Practitioner',
        ...practitioner, // Keep existing data if editing
        name: [
          {
            use: 'official',
            family: values.lastName,
            given: [values.firstName],
          },
        ],
        telecom,
        qualification:
          values.qualification || values.specialization
            ? [
                {
                  code: {
                    text: values.qualification || values.specialization,
                    coding: values.specialization
                      ? [
                          {
                            display: values.specialization,
                          },
                        ]
                      : undefined,
                  },
                },
              ]
            : undefined,
        // Always add extension to mark as external practitioner
        extension: [
          {
            url: ARKON.SD.EXTERNAL_ORGANIZATION_EXTENSION,
            valueString: values.organization || 'External',
          },
        ],
        // Add address if any address fields are provided
        address:
          values.addressLine1 ||
          values.city ||
          values.state ||
          values.postalCode ||
          values.country !== 'Greece'
            ? [
                {
                  use: 'work',
                  type: 'both',
                  line: [values.addressLine1, values.addressLine2].filter(
                    Boolean
                  ),
                  city: values.city || undefined,
                  state: values.state || undefined,
                  postalCode: values.postalCode || undefined,
                  country: values.country || undefined,
                },
              ]
            : undefined,
      };

      let submittedPractitioner: Practitioner;

      if (practitioner?.id) {
        // Update existing practitioner
        submittedPractitioner = await medplum.updateResource({
          ...practitionerData,
          id: practitioner.id,
        });
      } else {
        // Create new practitioner
        submittedPractitioner = await medplum.createResource(practitionerData);
      }

      notifications.show({
        color: 'green',
        icon: <IconCircleCheck />,
        title: t('notifications.success'),
        message: practitioner
          ? t('notifications.updated')
          : t('notifications.created'),
      });

      // If callback provided, call it instead of navigating
      if (onPractitionerCreated) {
        onPractitionerCreated(submittedPractitioner);
      } else if (submittedPractitioner?.id) {
        // Otherwise, navigate to the practitioner's page
        navigate(`/Practitioner/${submittedPractitioner.id}`);
      }

      close();
      form.reset();
    } catch (error) {
      console.error('Error creating/updating practitioner:', error);
      notifications.show({
        color: 'red',
        title: t('notifications.error'),
        message: practitioner
          ? t('notifications.updateFailed')
          : t('notifications.createFailed'),
      });
    } finally {
      setLoading(false);
    }
  };

  //----------------------------------------------------------------------------
  const handleOpen = () => {
    loadPractitionerData();
    open();
  };

  //----------------------------------------------------------------------------
  const hasUnsavedChanges = (): boolean => {
    try {
      return !isEqual(initialSnapshot, form.getValues());
    } catch {
      return true;
    }
  };

  const handleClose = () => {
    close();
    form.reset();
    setInitialSnapshot(null);
  };

  const handleDiscardChanges = () => {
    if (practitioner) {
      loadPractitionerData();
    } else {
      form.reset();
    }
    setInitialSnapshot(form.getValues());
  };

  //----------------------------------------------------------------------------
  return (
    <>
      {' '}
      <Modal
        opened={opened}
        onClose={() => formActionsRef.current?.handleGuardedClose()}
        size={600}
        title={
          <Stack gap={4}>
            <Text fw={700} size="lg">
              {practitioner ? t('modal.titleEdit') : t('modal.titleCreate')}
            </Text>
            {!practitioner && (
              <Text size="sm" c="dimmed">
                {t('modal.subtitleExternal')}
              </Text>
            )}
          </Stack>
        }
        centered
        closeOnClickOutside={false}
        closeOnEscape={false}
      >
        <form
          onSubmit={form.onSubmit(onSubmit)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.preventDefault();
          }}
        >
          <Stack gap="md">
            <Group grow>
              <TextInput
                label={t('form.firstName')}
                placeholder={t('placeholders.firstName')}
                required
                {...form.getInputProps('firstName')}
              />
              <TextInput
                label={t('form.lastName')}
                placeholder={t('placeholders.lastName')}
                required
                {...form.getInputProps('lastName')}
              />
            </Group>
            <TextInput
              label={t('form.email')}
              placeholder={t('placeholders.email')}
              type="email"
              {...form.getInputProps('email')}
            />
            <div>
              <Text size="sm" fw={500} mb={5}>
                {t('form.mobilePhone')}
              </Text>
              <PhoneInput
                country="gr"
                value={form.values.phone}
                onChange={(phone) => form.setFieldValue('phone', phone)}
                inputStyle={{
                  width: '100%',
                  height: '36px',
                  fontSize: '14px',
                }}
                containerStyle={{
                  width: '100%',
                }}
              />
              {form.errors.phone && (
                <Text size="xs" color="red" mt={5}>
                  {form.errors.phone}
                </Text>
              )}
            </div>
            <div>
              <Text size="sm" fw={500} mb={5}>
                {t('form.workPhone')}
              </Text>
              <PhoneInput
                country="gr"
                value={form.values.workPhone}
                onChange={(workPhone) =>
                  form.setFieldValue('workPhone', workPhone)
                }
                inputStyle={{
                  width: '100%',
                  height: '36px',
                  fontSize: '14px',
                }}
                containerStyle={{
                  width: '100%',
                }}
              />
            </div>
            <TextInput
              label={t('form.qualification')}
              placeholder={t('placeholders.qualification')}
              {...form.getInputProps('qualification')}
            />
            <TextInput
              label={t('form.specialization')}
              placeholder={t('placeholders.specialization')}
              {...form.getInputProps('specialization')}
            />{' '}
            <TextInput
              label={t('form.organization')}
              placeholder={t('placeholders.organization')}
              description={t('form.organizationDescription')}
              {...form.getInputProps('organization')}
            />
            {/* Address Section */}
            <Stack gap="md" mt="md">
              <Text fw={600} size="sm" c="dimmed">
                {t('form.addressSection')}
              </Text>

              <TextInput
                label={t('form.addressLine1')}
                placeholder={t('placeholders.addressLine1')}
                {...form.getInputProps('addressLine1')}
              />

              <TextInput
                label={t('form.addressLine2')}
                placeholder={t('placeholders.addressLine2')}
                {...form.getInputProps('addressLine2')}
              />

              <Group grow>
                <TextInput
                  label={t('form.city')}
                  placeholder={t('placeholders.city')}
                  {...form.getInputProps('city')}
                />
                <TextInput
                  label={t('form.state')}
                  placeholder={t('placeholders.state')}
                  {...form.getInputProps('state')}
                />
              </Group>

              <Group grow>
                <TextInput
                  label={t('form.postalCode')}
                  placeholder={t('placeholders.postalCode')}
                  {...form.getInputProps('postalCode')}
                />
                <TextInput
                  label={t('form.country')}
                  placeholder={t('placeholders.country')}
                  {...form.getInputProps('country')}
                />
              </Group>
            </Stack>
            <FormActionButtons
              ref={formActionsRef}
              onDiscard={handleDiscardChanges}
              onClose={handleClose}
              hasUnsavedChanges={hasUnsavedChanges()}
              isLoading={loading}
              saveLabel={
                practitioner ? t('buttons.update') : t('buttons.create')
              }
            />
          </Stack>
        </form>
      </Modal>
      {externalOpened === undefined && (
        <Button leftSection={<IconPlus size={16} />} onClick={handleOpen}>
          {t('buttons.createExternal')}
        </Button>
      )}
    </>
  );
};

export default CreatePractitionerModal;
